import { createSiweMessage } from "viem/siwe";
import { getAddress, isAddress } from "viem";
import { API_URL } from "./api";

const PROFILE_API_PREFIX = "/api/v1/x402/profiles";
const PROFILE_SITE_PREFIX = "/api/site/x402/profiles";
const BASE_ACCOUNT_PREFIX = "eip155:8453:";
const BASE_CHAIN_ID = 8_453;
const SIGN_IN_WITH_X = "SIGN-IN-WITH-X";

export type X402DiscoveryMode = "private" | "public_all";
export type X402ProfileProofKind = "eip191" | "eip1271" | "eip6492";
export type X402ActivityKind = "used" | "paid_settlement" | "reviewed" | "recognized";
export type X402ActivityEvidenceKind =
  | "receipt_settlement"
  | "receipt_only"
  | "client_settlement"
  | "settlement_only"
  | "boon_backed"
  | "receipt_verified"
  | "self_reported"
  | "routed_boon";

export interface X402ProfileWallet {
  accountId: string;
  address?: string;
  network?: "eip155:8453";
  proofKind: X402ProfileProofKind;
  linkedAt: number;
  proofDigest: string;
  discoveryConsent?: X402DiscoveryMode;
  isController?: boolean;
}

export interface X402Profile {
  id: string;
  controllerAccount?: string;
  displayHandle: string | null;
  discoveryMode: X402DiscoveryMode;
  publicationPolicy: string;
  walletSetVersion: number;
  publicSince: number | null;
  wallets: X402ProfileWallet[];
}

export interface X402LinkedIdentity {
  handle: string;
  provider: "github" | "x" | "agent";
  linkedWallet: string;
  profileUrl: string;
  verification: "boon_oauth_link" | "erc8004_indexed_at_recognition";
  linkedAt: string | null;
}

export interface X402ProfileActivity {
  id: string;
  kind: X402ActivityKind;
  accountId: string;
  routeId: string | null;
  origin: string | null;
  method: string | null;
  path: string | null;
  network: string | null;
  asset: string | null;
  amountAtomic: string | null;
  transaction: string | null;
  occurredAt: number;
  evidenceKind: X402ActivityEvidenceKind;
  details: Record<string, unknown> | null;
}

export interface X402PublicProfileResponse {
  version: "1";
  profile: X402Profile;
  /** Public profiles include recent facts. null remains accepted for older deployments. */
  activity: X402ProfileActivity[] | null;
  pagination: {
    limit: number;
    cursor: string | null;
    hasMore: boolean;
  } | null;
}

export interface X402WalletLink {
  id: string;
  status: "pending_member" | "active" | "expired" | "revoked";
  profileId: string;
  controllerAccount: string;
  memberAccount: string;
  intentDigest: string;
  discoveryMode: X402DiscoveryMode;
  publicationPolicy: string;
  expiresAt: number;
}

export interface X402WalletLinkPreparation {
  link: X402WalletLink;
  confirmation: {
    url: string;
    method: "POST";
    body: { intentDigest: string };
  };
}

export interface X402DiscoveryPreview {
  profileId: string;
  discoveryMode: X402DiscoveryMode;
  targetMode: "public_all";
  publicationPolicy: string;
  walletSetVersion: number;
  wallets: string[];
  factCounts: Record<string, number>;
  canPublishAll?: boolean;
  walletConsent?: Array<{
    accountId: string;
    discoveryConsent: X402DiscoveryMode;
    isController: boolean;
  }>;
  statement: string;
  digest: string;
}

export interface X402SiwxSigner {
  address: `0x${string}`;
  signMessage: (message: string) => Promise<`0x${string}`>;
}

interface SiwxInfo {
  domain: string;
  uri: string;
  statement?: string;
  version: string;
  nonce: string;
  issuedAt: string;
  expirationTime?: string;
  notBefore?: string;
  requestId?: string;
  resources?: string[];
}

interface SiwxExtension {
  info: SiwxInfo;
  supportedChains: Array<{ chainId: string; type: string; signatureScheme?: string }>;
}

export function toBaseAccountId(address: string): string {
  if (!isAddress(address)) throw new Error("Enter a valid EVM wallet address.");
  return `${BASE_ACCOUNT_PREFIX}${getAddress(address)}`;
}

export function baseAddressFromAccountId(accountId: string): `0x${string}` | null {
  if (!accountId.startsWith(BASE_ACCOUNT_PREFIX)) return null;
  const address = accountId.slice(BASE_ACCOUNT_PREFIX.length);
  return isAddress(address) ? getAddress(address) : null;
}

export async function fetchPublicX402Profile(profileId: string): Promise<X402PublicProfileResponse> {
  const value = await fetchJson(`${PROFILE_SITE_PREFIX}/${encodeURIComponent(profileId)}`);
  return parsePublicProfileResponse(value);
}

export async function fetchPublicX402ProfileByWallet(accountId: string): Promise<X402PublicProfileResponse> {
  const value = await fetchJson(`${PROFILE_SITE_PREFIX}/by-wallet/${encodeURIComponent(accountId)}`);
  return parsePublicProfileResponse(value);
}

export async function fetchX402LinkedIdentities(address: string): Promise<X402LinkedIdentity[]> {
  if (!isAddress(address)) throw new Error("Enter a valid EVM wallet address.");
  const expectedAddress = getAddress(address);
  const value = await fetchJson(`/api/site/link/wallet?address=${encodeURIComponent(expectedAddress)}`);
  if (!isRecord(value) || value.version !== "1" || typeof value.address !== "string" ||
      !isAddress(value.address) || getAddress(value.address) !== expectedAddress || !Array.isArray(value.accounts)) {
    throw new Error("The linked-identity response was malformed.");
  }
  return value.accounts.map((account) => {
    if (!isRecord(account) || typeof account.handle !== "string" ||
        (account.provider !== "github" && account.provider !== "x" && account.provider !== "agent") ||
        typeof account.linkedWallet !== "string" || !isAddress(account.linkedWallet) ||
        getAddress(account.linkedWallet) !== expectedAddress || !account.handle.startsWith(`${account.provider}:`)) {
      throw new Error("The linked-identity response was malformed.");
    }
    const expectedVerification = account.provider === "agent" ? "erc8004_indexed_at_recognition" : "boon_oauth_link";
    if (account.verification !== undefined && account.verification !== expectedVerification) {
      throw new Error("The linked-identity response was malformed.");
    }
    const username = account.handle.slice(account.provider.length + 1);
    const fallbackUrl = account.provider === "github"
      ? `https://github.com/${encodeURIComponent(username)}`
      : account.provider === "x"
        ? `https://x.com/${encodeURIComponent(username)}`
        : `https://www.8004scan.io/agents/base/${encodeURIComponent(username)}`;
    const profileUrl = typeof account.profileUrl === "string" ? safeLinkedProfileUrl(account.profileUrl, account.provider) : fallbackUrl;
    if (!profileUrl) throw new Error("The linked-identity response was malformed.");
    return {
      handle: account.handle,
      provider: account.provider,
      linkedWallet: expectedAddress,
      profileUrl,
      verification: expectedVerification,
      linkedAt: typeof account.linkedAt === "string" && /^[0-9]+$/.test(account.linkedAt) ? account.linkedAt : null,
    };
  });
}

export async function createX402Profile(signer: X402SiwxSigner): Promise<X402Profile> {
  const value = await fetchSiwxJson(PROFILE_API_PREFIX, signer, {
    method: "POST",
    body: JSON.stringify({ controllerAccount: toBaseAccountId(signer.address) }),
  });
  return parseProfileEnvelope(value);
}

export async function fetchPrivateX402Profile(profileId: string, signer: X402SiwxSigner): Promise<X402Profile> {
  const value = await fetchSiwxJson(`${PROFILE_API_PREFIX}/${encodeURIComponent(profileId)}`, signer);
  return parseProfileEnvelope(value);
}

export async function prepareX402WalletLink(
  profileId: string,
  memberAccount: string,
  signer: X402SiwxSigner,
): Promise<X402WalletLinkPreparation> {
  const value = await fetchSiwxJson(
    `${PROFILE_API_PREFIX}/${encodeURIComponent(profileId)}/wallet-links`,
    signer,
    { method: "POST", body: JSON.stringify({ memberAccount }) },
  );
  if (!isRecord(value) || value.version !== "1" || !isWalletLink(value.link) || !isRecord(value.confirmation) ||
      typeof value.confirmation.url !== "string" || value.confirmation.method !== "POST" ||
      !isRecord(value.confirmation.body) || typeof value.confirmation.body.intentDigest !== "string") {
    throw new Error("The wallet-link preparation returned malformed data.");
  }
  if (value.link.profileId !== profileId || value.link.status !== "pending_member" ||
      value.link.memberAccount.toLowerCase() !== memberAccount.toLowerCase() ||
      value.link.controllerAccount.toLowerCase() !== toBaseAccountId(signer.address).toLowerCase()) {
    throw new Error("The wallet-link preparation did not match the requested profile and wallets.");
  }
  if (value.confirmation.body.intentDigest !== value.link.intentDigest) {
    throw new Error("The wallet-link confirmation did not match the prepared intent.");
  }
  const confirmationUrl = validateConfirmationUrl(value.confirmation.url, value.link);
  return {
    link: value.link,
    confirmation: {
      url: confirmationUrl,
      method: "POST",
      body: { intentDigest: value.confirmation.body.intentDigest },
    },
  };
}

export async function revokeX402WalletLink(
  profileId: string,
  accountId: string,
  signer: X402SiwxSigner,
): Promise<X402Profile> {
  const value = await fetchSiwxJson(
    `${PROFILE_API_PREFIX}/${encodeURIComponent(profileId)}/wallet-links/${encodeURIComponent(accountId)}`,
    signer,
    {
      method: "DELETE",
      body: JSON.stringify({ actorAccount: toBaseAccountId(signer.address) }),
    },
  );
  if (!isRecord(value) || value.version !== "1" || value.revoked !== true || !isProfile(value.profile)) {
    throw new Error("The wallet-link revocation returned malformed data.");
  }
  return value.profile;
}

export async function fetchX402DiscoveryPreview(
  profileId: string,
  signer: X402SiwxSigner,
): Promise<X402DiscoveryPreview> {
  const value = await fetchSiwxJson(
    `${PROFILE_API_PREFIX}/${encodeURIComponent(profileId)}/discovery/preview`,
    signer,
  );
  if (!isRecord(value) || value.version !== "1" || !isDiscoveryPreview(value.preview)) {
    throw new Error("The discovery preview returned malformed data.");
  }
  return value.preview;
}

export async function setX402DiscoveryMode(
  profileId: string,
  discoveryMode: X402DiscoveryMode,
  signer: X402SiwxSigner,
  previewDigest?: string,
): Promise<{ profile: X402Profile; preview: X402DiscoveryPreview | null }> {
  const value = await fetchSiwxJson(
    `${PROFILE_API_PREFIX}/${encodeURIComponent(profileId)}/discovery`,
    signer,
    {
      method: "PUT",
      body: JSON.stringify({ discoveryMode, ...(previewDigest ? { previewDigest } : {}) }),
    },
  );
  if (!isRecord(value) || value.version !== "1" || !isProfile(value.profile) ||
      !(value.preview === null || value.preview === undefined || isDiscoveryPreview(value.preview))) {
    throw new Error("The discovery update returned malformed data.");
  }
  return { profile: value.profile, preview: value.preview ?? null };
}

export function agentCashConfirmationCommand(preparation: X402WalletLinkPreparation): string {
  const confirmationUrl = validateConfirmationUrl(preparation.confirmation.url, preparation.link);
  if (preparation.confirmation.body.intentDigest !== preparation.link.intentDigest) {
    throw new Error("The wallet-link confirmation did not match the prepared intent.");
  }
  const body = JSON.stringify(preparation.confirmation.body);
  return `npx agentcash@latest fetch ${posixShellQuote(confirmationUrl)} -m POST -b ${posixShellQuote(body)} --payment-network base --payment-protocol x402 --max-amount 0.000000000001 --format json`;
}

/**
 * Retry one free identity operation with a CAIP-122/SIWE proof. The first 402
 * is only a challenge. No x402 payment payload is created and no funds move.
 */
export async function fetchSiwxJson(path: string, signer: X402SiwxSigner, init: RequestInit = {}): Promise<unknown> {
  const url = `${API_URL}${path}`;
  const headers = jsonHeaders(init.headers);
  const firstResponse = await fetch(url, { ...init, headers });
  if (firstResponse.ok) return firstResponse.json();
  if (firstResponse.status !== 402) throw await responseError(path, firstResponse);

  const paymentRequired = decodePaymentRequired(firstResponse.headers.get("payment-required"));
  const extension = readSiwxExtension(paymentRequired);
  const chain = extension.supportedChains.find((candidate) => candidate.chainId === `${BASE_ACCOUNT_PREFIX.slice(0, -1)}` && candidate.type === "eip191");
  if (!chain) throw new Error("This operation did not offer Base SIWX authentication.");
  validateSiwxChallenge(extension.info, url);

  const address = getAddress(signer.address);
  const message = createSiweMessage({
    address,
    chainId: BASE_CHAIN_ID,
    domain: extension.info.domain,
    nonce: extension.info.nonce,
    uri: extension.info.uri,
    version: "1",
    statement: extension.info.statement,
    issuedAt: new Date(extension.info.issuedAt),
    expirationTime: extension.info.expirationTime ? new Date(extension.info.expirationTime) : undefined,
    notBefore: extension.info.notBefore ? new Date(extension.info.notBefore) : undefined,
    requestId: extension.info.requestId,
    resources: extension.info.resources,
  });
  const signature = await signer.signMessage(message);
  const proof = {
    ...extension.info,
    address,
    chainId: chain.chainId,
    type: "eip191",
    signatureScheme: chain.signatureScheme,
    signature,
  };
  const retryHeaders = jsonHeaders(init.headers);
  retryHeaders.set(SIGN_IN_WITH_X, encodeBase64(JSON.stringify(proof)));
  const response = await fetch(url, { ...init, headers: retryHeaders });
  if (!response.ok) throw await responseError(path, response);
  return response.json();
}

function parsePublicProfileResponse(value: unknown): X402PublicProfileResponse {
  if (!isRecord(value) || value.version !== "1" || !isProfile(value.profile)) {
    throw new Error("The x402 profile returned malformed data.");
  }
  const activity = value.activity === undefined ? null : value.activity;
  if (!(activity === null || (Array.isArray(activity) && activity.every(isActivity)))) {
    throw new Error("The x402 profile activity returned malformed data.");
  }
  const pagination = value.pagination === undefined ? null : value.pagination;
  if (!(pagination === null || (isRecord(pagination) && isCount(pagination.limit) &&
      (pagination.cursor === null || typeof pagination.cursor === "string") && typeof pagination.hasMore === "boolean"))) {
    throw new Error("The x402 profile pagination returned malformed data.");
  }
  return {
    version: "1",
    profile: value.profile,
    activity,
    pagination: pagination === null ? null : {
      limit: pagination.limit as number,
      cursor: pagination.cursor as string | null,
      hasMore: pagination.hasMore as boolean,
    },
  };
}

function parseProfileEnvelope(value: unknown): X402Profile {
  if (!isRecord(value) || value.version !== "1" || !isProfile(value.profile)) {
    throw new Error("The x402 profile operation returned malformed data.");
  }
  return value.profile;
}

function isProfile(value: unknown): value is X402Profile {
  if (!isRecord(value) || typeof value.id !== "string" ||
      !(value.controllerAccount === undefined || typeof value.controllerAccount === "string") ||
      !(value.displayHandle === null || typeof value.displayHandle === "string") ||
      !(value.discoveryMode === "private" || value.discoveryMode === "public_all") ||
      typeof value.publicationPolicy !== "string" || !isCount(value.walletSetVersion) ||
      !(value.publicSince === null || isCount(value.publicSince)) || !Array.isArray(value.wallets) ||
      !value.wallets.every(isProfileWallet)) return false;
  return true;
}

function isProfileWallet(value: unknown): value is X402ProfileWallet {
  return isRecord(value) && typeof value.accountId === "string" &&
    (value.proofKind === "eip191" || value.proofKind === "eip1271" || value.proofKind === "eip6492") &&
    isCount(value.linkedAt) && typeof value.proofDigest === "string" &&
    (value.address === undefined || typeof value.address === "string") &&
    (value.network === undefined || value.network === "eip155:8453") &&
    (value.discoveryConsent === undefined || value.discoveryConsent === "private" || value.discoveryConsent === "public_all") &&
    (value.isController === undefined || typeof value.isController === "boolean");
}

function isWalletLink(value: unknown): value is X402WalletLink {
  return isRecord(value) && typeof value.id === "string" &&
    (value.status === "pending_member" || value.status === "active" || value.status === "expired" || value.status === "revoked") &&
    typeof value.profileId === "string" && typeof value.controllerAccount === "string" &&
    typeof value.memberAccount === "string" && typeof value.intentDigest === "string" &&
    (value.discoveryMode === "private" || value.discoveryMode === "public_all") &&
    typeof value.publicationPolicy === "string" && isCount(value.expiresAt);
}

function isDiscoveryPreview(value: unknown): value is X402DiscoveryPreview {
  return isRecord(value) && typeof value.profileId === "string" &&
    (value.discoveryMode === "private" || value.discoveryMode === "public_all") && value.targetMode === "public_all" &&
    typeof value.publicationPolicy === "string" && isCount(value.walletSetVersion) &&
    Array.isArray(value.wallets) && value.wallets.every((wallet) => typeof wallet === "string") && isRecord(value.factCounts) &&
    Object.values(value.factCounts).every(isCount) && typeof value.statement === "string" && typeof value.digest === "string" &&
    (value.canPublishAll === undefined || typeof value.canPublishAll === "boolean") &&
    (value.walletConsent === undefined || (Array.isArray(value.walletConsent) && value.walletConsent.every((entry) =>
      isRecord(entry) && typeof entry.accountId === "string" &&
      (entry.discoveryConsent === "private" || entry.discoveryConsent === "public_all") && typeof entry.isController === "boolean")));
}

function validateConfirmationUrl(value: string, link: X402WalletLink): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error("The wallet-link confirmation URL was invalid.");
  }
  const expectedPath = `/api/v1/x402/profiles/${encodeURIComponent(link.profileId)}/wallet-links/${encodeURIComponent(link.id)}/confirm`;
  if (url.protocol !== "https:" || url.origin !== "https://api.boonprotocol.com" || url.pathname !== expectedPath ||
      url.username || url.password || url.search || url.hash || url.href !== `https://api.boonprotocol.com${expectedPath}`) {
    throw new Error("The wallet-link confirmation URL did not match Boon's exact API route.");
  }
  return url.href;
}

function posixShellQuote(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

function isActivity(value: unknown): value is X402ProfileActivity {
  return isRecord(value) && typeof value.id === "string" &&
    (value.kind === "used" || value.kind === "paid_settlement" || value.kind === "reviewed" || value.kind === "recognized") &&
    typeof value.accountId === "string" && (value.routeId === null || typeof value.routeId === "string") &&
    isCount(value.occurredAt) && nullableString(value.origin) && nullableString(value.method) && nullableString(value.path) &&
    nullableString(value.network) && nullableString(value.asset) && nullableString(value.amountAtomic) && nullableString(value.transaction) &&
    isActivityEvidenceKind(value.evidenceKind) && (value.details === null || isRecord(value.details));
}

function isActivityEvidenceKind(value: unknown): value is X402ActivityEvidenceKind {
  return value === "receipt_settlement" || value === "receipt_only" || value === "client_settlement" || value === "settlement_only" ||
    value === "boon_backed" || value === "receipt_verified" || value === "self_reported" || value === "routed_boon";
}

function nullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function decodePaymentRequired(header: string | null): unknown {
  if (!header) throw new Error("The server did not return a SIWX challenge.");
  try {
    return JSON.parse(decodeBase64(header));
  } catch {
    throw new Error("The server returned a malformed SIWX challenge.");
  }
}

function readSiwxExtension(value: unknown): SiwxExtension {
  const extension = isRecord(value) && isRecord(value.extensions) ? value.extensions["sign-in-with-x"] : null;
  if (!isRecord(extension) || !isRecord(extension.info) || !Array.isArray(extension.supportedChains)) {
    throw new Error("The server did not offer SIWX authentication.");
  }
  const info = extension.info;
  if (typeof info.domain !== "string" || typeof info.uri !== "string" || typeof info.version !== "string" ||
      typeof info.nonce !== "string" || typeof info.issuedAt !== "string" || !optionalString(info.statement) ||
      !optionalString(info.expirationTime) || !optionalString(info.notBefore) || !optionalString(info.requestId) ||
      !(info.resources === undefined || (Array.isArray(info.resources) && info.resources.every((item) => typeof item === "string")))) {
    throw new Error("The server returned incomplete SIWX challenge fields.");
  }
  const supportedChains = extension.supportedChains.filter((candidate): candidate is SiwxExtension["supportedChains"][number] =>
    isRecord(candidate) && typeof candidate.chainId === "string" && typeof candidate.type === "string" && optionalString(candidate.signatureScheme),
  );
  if (supportedChains.length !== extension.supportedChains.length) throw new Error("The server returned malformed SIWX chain support.");
  return { info: info as unknown as SiwxInfo, supportedChains };
}

function validateSiwxChallenge(info: SiwxInfo, expectedUrl: string): void {
  const expected = new URL(expectedUrl);
  const pageHost = typeof globalThis.location?.host === "string"
    ? globalThis.location.host
    : "boonprotocol.com";
  if (info.domain !== pageHost || info.uri !== expected.href || info.version !== "1") {
    throw new Error("The SIWX challenge did not match the requested Boon operation.");
  }
  if (info.resources && !info.resources.includes(expected.href)) {
    throw new Error("The SIWX challenge did not bind the requested resource.");
  }
  const issuedAt = Date.parse(info.issuedAt);
  const expiresAt = info.expirationTime ? Date.parse(info.expirationTime) : NaN;
  if (!Number.isFinite(issuedAt) || Date.now() - issuedAt > 5 * 60 * 1_000 || issuedAt - Date.now() > 30_000) {
    throw new Error("The SIWX challenge issuance time is invalid.");
  }
  if (!Number.isFinite(expiresAt) || expiresAt <= Date.now() || expiresAt - issuedAt > 10 * 60 * 1_000) {
    throw new Error("The SIWX challenge expiration is invalid.");
  }
}

function jsonHeaders(input?: HeadersInit): Headers {
  const headers = new Headers(input);
  headers.set("accept", "application/json");
  if (!headers.has("content-type")) headers.set("content-type", "application/json");
  return headers;
}

async function fetchJson(path: string): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, { headers: { accept: "application/json" } });
  if (!response.ok) throw await responseError(path, response);
  return response.json();
}

async function responseError(path: string, response: Response): Promise<Error> {
  const body = await response.json().catch(() => null) as { error?: unknown } | null;
  const detail = body && typeof body.error === "string" ? `: ${body.error}` : "";
  return new Error(`${path} returned ${response.status}${detail}`);
}

function encodeBase64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  return btoa(Array.from(bytes, (byte) => String.fromCharCode(byte)).join(""));
}

function decodeBase64(value: string): string {
  const binary = atob(value);
  return new TextDecoder().decode(Uint8Array.from(binary, (character) => character.charCodeAt(0)));
}

function safeLinkedProfileUrl(value: string, provider: X402LinkedIdentity["provider"]): string | null {
  try {
    const url = new URL(value);
    const expectedHost = provider === "github" ? "github.com" : provider === "x" ? "x.com" : "www.8004scan.io";
    if (url.protocol !== "https:" || url.hostname !== expectedHost || url.username || url.password || url.search || url.hash) return null;
    return url.href;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function optionalString(value: unknown): boolean {
  return value === undefined || value === null || typeof value === "string";
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
